import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { ventasReportService } from '../../services/ventasReportService';

function defaultDateRange() {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 5);
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { date_from: fmt(from), date_to: fmt(to) };
}

const formatMoney = (value) =>
  `S/ ${Number(value || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function DashboardVentasSection() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);

  const range = useMemo(() => defaultDateRange(), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await ventasReportService.getDashboard({
          date_from: range.date_from,
          date_to: range.date_to,
        });
        if (cancelled) return;
        if (res.success) {
          setData(res.data);
          setMeta(res.meta);
        } else {
          setError(res.message || 'No se pudo cargar ventas');
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || 'Error al cargar resumen de ventas');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [range.date_from, range.date_to]);

  const kpis = data?.kpis;
  const monthly = data?.monthly_products_services || [];
  const hasProducts = monthly.some((row) => Number(row.products) > 0)
    || Number(kpis?.product_revenue) > 0;
  const hasServices = monthly.some((row) => Number(row.services) > 0)
    || Number(kpis?.service_revenue) > 0;
  const hasChart = monthly.length > 0 && (hasProducts || hasServices);

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8 border-l-4 border-violet-500">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Ventas — último semestre</h3>
          <p className="text-sm text-gray-500 mt-1">
            Montos en soles (órdenes de venta y órdenes de servicio emitidas).
            {meta ? ` Período ${meta.date_from} → ${meta.date_to}.` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-sm">
          <Link to="/ventas/orders" className="font-medium text-violet-700 hover:text-violet-900">
            Órdenes de venta →
          </Link>
          <Link to="/ventas/service-orders" className="font-medium text-violet-700 hover:text-violet-900">
            Órdenes de servicio →
          </Link>
          <Link to="/ventas/reports/ventas" className="font-medium text-teal-700 hover:text-teal-900">
            Dashboard POS →
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-gray-500 text-sm">
          <span className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600 mr-2" />
          Cargando ventas…
        </div>
      ) : error ? (
        <p className="text-sm text-red-600 py-4">{error}</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="rounded-lg bg-violet-50 p-4">
              <p className="text-xs text-violet-800 font-medium uppercase tracking-wide">Ventas netas</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{formatMoney(kpis?.sales_net)}</p>
              <p className="text-xs text-gray-600 mt-1">
                {kpis?.orders_count || 0} órdenes
                {(kpis?.sales_orders_count > 0 || kpis?.service_orders_count > 0) && (
                  <>
                    {' '}
                    ({kpis?.sales_orders_count || 0} venta
                    {(kpis?.service_orders_count || 0) > 0
                      ? `, ${kpis.service_orders_count} servicio`
                      : ''}
                    )
                  </>
                )}
              </p>
            </div>
            <div className="rounded-lg bg-indigo-50 p-4">
              <p className="text-xs text-indigo-800 font-medium uppercase tracking-wide">Total con IGV</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{formatMoney(kpis?.sales_gross)}</p>
            </div>
            {hasProducts ? (
              <div className="rounded-lg bg-emerald-50 p-4">
                <p className="text-xs text-emerald-800 font-medium uppercase tracking-wide">Productos</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{formatMoney(kpis?.product_revenue)}</p>
              </div>
            ) : null}
            {hasServices ? (
              <div className="rounded-lg bg-blue-50 p-4">
                <p className="text-xs text-blue-800 font-medium uppercase tracking-wide">Servicios</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{formatMoney(kpis?.service_revenue)}</p>
              </div>
            ) : null}
            <div className="rounded-lg bg-slate-50 p-4">
              <p className="text-xs text-slate-600 font-medium uppercase tracking-wide">Ticket promedio</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{formatMoney(kpis?.avg_ticket)}</p>
            </div>
          </div>

          {hasChart ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={80} />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickFormatter={(v) =>
                    `S/ ${Number(v).toLocaleString('es-PE', { maximumFractionDigits: 0 })}`
                  }
                />
                <Tooltip
                  formatter={(value, name) => {
                    const labels = { products: 'Productos', services: 'Servicios' };
                    return [formatMoney(value), labels[name] || name];
                  }}
                />
                <Legend
                  formatter={(value) =>
                    ({ products: 'Productos', services: 'Servicios' }[value] || value)
                  }
                />
                {hasProducts ? (
                  <Line
                    type="monotone"
                    dataKey="products"
                    name="products"
                    stroke="#10B981"
                    strokeWidth={2}
                    dot={{ r: 5 }}
                  />
                ) : null}
                {hasServices ? (
                  <Line
                    type="monotone"
                    dataKey="services"
                    name="services"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    dot={{ r: 5 }}
                  />
                ) : null}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-gray-500 text-center py-8">
              No hay órdenes de venta ni de servicio emitidas en el período.
            </p>
          )}
        </>
      )}
    </div>
  );
}
